import { useAutoAnimate } from '@formkit/auto-animate/react';
import { FireIcon, SparklesIcon } from '@heroicons/react/20/solid';
import {
  QuestionMarkCircleIcon,
  RectangleGroupIcon
} from '@heroicons/react/24/outline';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import axios from 'axios';
import Tag from 'components/common/tag';
import Button from 'components/inputs/button';
import Checkbox from 'components/inputs/checkbox';
import Skeleton from 'components/layout/skeleton';
import Tooltip from 'components/layout/tooltip';
import ProductLink from 'components/products/product-link';
import ProductPhoto from 'components/products/product-photo';
import EmailSingleQuoteModal from 'components/quote-page/modals/email-single-quote-modal';
import SystemMap from 'components/systems/system-map';
import ZoneDuctingProducts from 'components/systems/zone-ducting-products';
import {
  expandProductIds,
  useAlpineCartContext
} from 'contexts/alpine-cart-context';
import { RebateType, SelectedSystemType, ShippingInfoType } from 'data/types';
import {
  useGetProductStockStatusQuery,
  useGetQuotableSystemsQuery
} from 'features/api/project-api';
import { updateSelectedSystem } from 'features/systems';
import { setQuoteIDsToSend, setRebatesForModal } from 'features/ui';
import { produce } from 'immer';
import {
  Dispatch,
  MouseEvent,
  SetStateAction,
  useEffect,
  useMemo,
  useState
} from 'react';
import InfoTooltip from 'components/layout/info-tooltip';
import { estimatedBaselineSeerOperatingCost } from 'utils/baseline-seer-operating-cost';
import { COMPATIBILITY_MATCH_TAG } from 'utils/compatibility-systems';
import { KEPT_EQUIPMENT_PRODUCT_ID } from 'utils/equipment-placeholder';
import { formatCoverageScore } from 'utils/coverage-score-format';
import {
  ductedCoverageTooltipText,
  ductedPercentLabel,
  getDuctedCoverage
} from 'utils/ducted-coverage';
import { isConcealedDuctProduct } from 'utils/ducting-products';
import { formatUSD } from 'utils/helpers';
import { buildZoneQuotedAccessoriesMap } from 'utils/quotable-system-accessories';
import { errorModal, notification, warningModal } from 'utils/ui';

type QuoteColumnProps = {
  system: SelectedSystemType;
  systemIndex: number;
  handleQuoteColumnClick: (systemIndex: number) => void;
  setRebatesModalOpen: Dispatch<SetStateAction<boolean>>;
  optionIndex: number | null;
  setOptionIndex: Dispatch<SetStateAction<number | null>>;
  isQuoted?: boolean;
  quoteId?: string;
};

export default function QuoteColumn({
  system,
  systemIndex,
  handleQuoteColumnClick,
  setRebatesModalOpen,
  optionIndex,
  setOptionIndex,
  isQuoted,
  quoteId
}: QuoteColumnProps) {
  const dispatch = useAppDispatch();
  const { locationData, projectDetails } = useAppSelector(
    (state) => state.project
  );
  const { quoteIDsToSend } = useAppSelector((state) => state.ui);
  const [showAllItems, setShowAllItems] = useState(true);
  const [animate] = useAutoAnimate({ duration: 150 });

  const [localTax, setLocalTax] = useState<number | undefined>(undefined);
  const [localShipping, setLocalShipping] = useState<
    ShippingInfoType | undefined
  >(undefined);
  const [localRebates, setLocalRebates] = useState<RebateType[] | undefined>(
    undefined
  );
  const [localTotalRebates, setLocalTotalRebates] = useState<
    number | undefined
  >(undefined);

  const effectiveTax = localTax ?? system.tax;
  const effectiveShipping = localShipping ?? system.shipping;
  const effectiveRebates = localRebates ?? system.rebates;
  const effectiveTotalRebates = localTotalRebates ?? system.totalRebates;

  // Ducted design-load coverage (AHA-6796): presence switches the coverage
  // section to "Coverage of Design Load" with the design-load explainer.
  const ductedCoverage = getDuctedCoverage(system);

  const isKeptProduct = (productId: number | undefined | null): boolean =>
    !productId || productId === KEPT_EQUIPMENT_PRODUCT_ID;

  const productIds = useMemo(() => {
    if (!isQuoted) return [];
    return [
      ...system.configuration.condensers
        .filter((c) => !isKeptProduct(c.selectedProduct?.productId))
        .map((c) => c.selectedProduct!.productId),
      ...system.configuration.condensers.flatMap((c) =>
        c.zones.flatMap((z) => [
          ...(!isKeptProduct(z.selectedProduct?.productId)
            ? [z.selectedProduct!.productId]
            : []),
          ...(!isKeptProduct(z.blowerProduct?.productId)
            ? [z.blowerProduct!.productId]
            : [])
        ])
      ),
      ...system.quotableAccessories
        .filter((acc) => acc.quantity > 0 && !isKeptProduct(acc.productId))
        .map((acc) => acc.productId)
    ];
  }, [isQuoted, system.configuration, system.quotableAccessories]);

  const { data: stockStatus } = useGetProductStockStatusQuery(productIds, {
    skip: !isQuoted || productIds.length === 0
  });

  const { data: quotableSystemsData, isError: quotableSystemsFailed } =
    useGetQuotableSystemsQuery([system.uniqueId], {
      skip: !system.uniqueId
    });

  const zoneQuotedAccessories = useMemo(() => {
    const accessories = quotableSystemsData?.find(
      (qs) => qs.uniqueId === system.uniqueId
    )?.accessories;
    const condensers = system.configuration.condensers.map((condenser) => ({
      condenserUniqueId: condenser.uniqueId,
      zoneUniqueIds: condenser.zones.map((zone) => zone.uniqueId)
    }));
    const zoneMap = buildZoneQuotedAccessoriesMap(
      accessories ?? [],
      condensers
    );
    return new Map(Object.entries(zoneMap));
  }, [quotableSystemsData, system.uniqueId, system.configuration.condensers]);

  const concealedZoneCount = useMemo(
    () =>
      system.configuration.condensers.reduce(
        (count, condenser) =>
          count +
          condenser.zones.filter((zone) =>
            isConcealedDuctProduct(zone.selectedProduct)
          ).length,
        0
      ),
    [system.configuration.condensers]
  );

  const effectiveInStock = isQuoted
    ? stockStatus?.inStock ?? undefined
    : system.inStock;
  const effectiveRestockDate = isQuoted
    ? stockStatus?.restockDate ?? undefined
    : system.restockDate;

  const {
    pasteToCart,
    startShopping,
    isLoading: isAddingToCart
  } = useAlpineCartContext();

  const emailModalOpenState = useState(false);
  const [emailModalOpen, setEmailModalOpen] = emailModalOpenState;

  const getTax = async () => {
    if (locationData) {
      const payload = JSON.stringify({
        destinationZip: locationData.zipcode,
        shippingcost: 0,
        products: [
          ...system.configuration.condensers
            .filter((c) => !isKeptProduct(c.selectedProduct?.productId))
            .map((c) => ({
              id: c.selectedProduct?.productId,
              quantity: 1
            })),
          ...system.configuration.condensers.flatMap((c) =>
            c.zones.flatMap((z) => [
              ...(!isKeptProduct(z.selectedProduct?.productId)
                ? [{ id: z.selectedProduct?.productId, quantity: 1 }]
                : []),
              ...(!isKeptProduct(z.blowerProduct?.productId)
                ? [{ id: z.blowerProduct!.productId, quantity: 1 }]
                : [])
            ])
          ),
          ...system.quotableAccessories.map((acc) => ({
            id: acc.productId,
            quantity: acc.quantity
          }))
        ]
      });
      try {
        const newTax = (
          await axios.post(
            `${process.env.REACT_APP_PARTNERNET_API_URL}/v1/api/cart/tax`,
            payload
          )
        ).data as number;
        return newTax;
      } catch (error) {
        console.error(error);
        errorModal('Unable to calculate tax.');
        return null;
      }
    }
    return null;
  };

  const getShippingInfo = async () => {
    if (locationData) {
      const payload = JSON.stringify({
        zipcode: locationData.zipcode,
        products: [
          ...system.configuration.condensers
            .filter((c) => !isKeptProduct(c.selectedProduct?.productId))
            .map((c) => ({
              id: c.selectedProduct?.productId,
              qty: 1
            })),
          ...system.configuration.condensers.flatMap((c) =>
            c.zones.flatMap((z) => [
              ...(!isKeptProduct(z.selectedProduct?.productId)
                ? [{ id: z.selectedProduct?.productId, qty: 1 }]
                : []),
              ...(!isKeptProduct(z.blowerProduct?.productId)
                ? [{ id: z.blowerProduct!.productId, qty: 1 }]
                : [])
            ])
          ),
          ...system.quotableAccessories.map((acc) => ({
            id: acc.productId,
            qty: acc.quantity
          }))
        ]
      });
      try {
        const newShippingInfo = (
          await axios.post(
            `${process.env.REACT_APP_PARTNERNET_API_URL}/v1/api/shipping/info`,
            payload
          )
        ).data as ShippingInfoType;
        return newShippingInfo;
      } catch (error) {
        console.error(error);
        errorModal('Unable to get shipping information.');
        return null;
      }
    }
    return null;
  };

  const getRebates = async () => {
    if (locationData) {
      const rebateInfo: RebateType[] = [];

      // TODO: Add new rebate info
      return rebateInfo;
    }
    return null;
  };

  const estimated14SeerCost = estimatedBaselineSeerOperatingCost(system);

  useEffect(() => {
    if (optionIndex !== null) {
      setOptionIndex(optionIndex);
    }
  }, [optionIndex]);

  useEffect(() => {
    const needsFetch = isQuoted
      ? !localTax || !localShipping
      : !system.tax || !system.shipping;

    if (needsFetch) {
      const taxPromise = getTax();
      const shippingPromise = getShippingInfo();
      const rebatesPromise = getRebates();
      Promise.all([taxPromise, shippingPromise, rebatesPromise]).then(
        ([taxInfo, shippingInfo, rebatesInfo]) => {
          let totalRebates = 0;
          if (rebatesInfo) {
            const productQuantities = system.configuration.condensers
              .flatMap((c) => [
                ...(!isKeptProduct(c.selectedProduct?.productId)
                  ? [{ id: c.selectedProduct?.productId, quantity: 1 }]
                  : []),
                ...c.zones.flatMap((z) => [
                  ...(!isKeptProduct(z.selectedProduct?.productId)
                    ? [{ id: z.selectedProduct?.productId, quantity: 1 }]
                    : []),
                  ...(!isKeptProduct(z.blowerProduct?.productId)
                    ? [{ id: z.blowerProduct!.productId, quantity: 1 }]
                    : [])
                ])
              ])
              .reduce((acc, product) => {
                if (product.id) {
                  acc[product.id] = (acc[product.id] || 0) + product.quantity;
                }
                return acc;
              }, {});

            totalRebates = rebatesInfo.reduce((sum, rebate) => {
              const quantity = productQuantities[rebate.productId] || 0;
              return (
                sum +
                Math.max(rebate.rebateMinimum, rebate.rebateMaximum) * quantity
              );
            }, 0);
          }

          if (isQuoted) {
            setLocalTax(taxInfo ?? undefined);
            setLocalShipping(shippingInfo ?? undefined);
            setLocalRebates(rebatesInfo ?? undefined);
            setLocalTotalRebates(totalRebates);
          } else {
            dispatch(
              updateSelectedSystem({
                uniqueId: system.uniqueId,
                tax: taxInfo ?? undefined,
                shipping: shippingInfo ?? undefined,
                rebates: rebatesInfo ?? undefined,
                totalRebates
              })
            );
          }
        }
      );
    }
  }, [system, isQuoted]);

  const handleColumnClick = () => {
    if (!quoteIDsToSend.includes(system.uniqueId)) {
      if (quoteIDsToSend.length >= 3) {
        warningModal('You can only include up to 3 systems per quote');
        return;
      }
      dispatch(
        setQuoteIDsToSend(
          produce(quoteIDsToSend, (oldValue) => [...oldValue, system.uniqueId])
        )
      );
    } else {
      dispatch(
        setQuoteIDsToSend(
          produce(quoteIDsToSend, (oldValue) =>
            oldValue.filter((id) => id !== system.uniqueId)
          )
        )
      );
    }
  };

  const handleShowAllItems = () => {
    setShowAllItems((oldValue) => !oldValue);
  };

  const getBuyNowButtonText = () => {
    if (isQuoted && quoteId) return 'View Quote';
    if (isAddingToCart) return 'Adding to Cart...';
    return 'Buy Now';
  };

  const handleBuyNow = () => {
    if (isQuoted && quoteId) {
      const quoteUrl = `${process.env.REACT_APP_ALPINE_CLIENT_URL}/view-quotes/${quoteId}`;

      // Open the quote page immediately (no about:blank flash)
      window.open(quoteUrl, '_blank');

      // Fire-and-forget: set customer/project context in the background.
      // The context only matters at checkout, not on the view-quotes page,
      // so it will be set well before the user navigates there.
      if (projectDetails?.projectId && projectDetails?.customerId) {
        startShopping(
          projectDetails.projectId,
          projectDetails.customerId
        ).catch(() => {
          // Silent — user will see the customer/project modal at checkout if this fails
        });
      }

      return;
    }

    const productsWithQuantities: Array<{ id: string; qty: number }> = [
      ...system.configuration.condensers
        .filter((c) => !isKeptProduct(c.selectedProduct?.productId))
        .map((c) => ({
          id: c.selectedProduct!.productId.toString(),
          qty: 1
        })),
      ...system.configuration.condensers.flatMap((c) =>
        c.zones.flatMap((z) => [
          ...(!isKeptProduct(z.selectedProduct?.productId)
            ? [{ id: z.selectedProduct!.productId.toString(), qty: 1 }]
            : []),
          ...(!isKeptProduct(z.blowerProduct?.productId)
            ? [{ id: z.blowerProduct!.productId.toString(), qty: 1 }]
            : [])
        ])
      ),
      ...system.quotableAccessories
        .filter((acc) => acc.quantity > 0 && acc.productId)
        .map((acc) => ({
          id: acc.productId.toString(),
          qty: acc.quantity
        }))
    ];

    if (productsWithQuantities.length === 0) {
      notification('No products to add to cart', 'warning');
      return;
    }

    const expandedProductIds = expandProductIds(productsWithQuantities);

    pasteToCart(expandedProductIds, {
      clearCart: true,
      redirectUrl: `${process.env.REACT_APP_ALPINE_CLIENT_URL}/cart`,
      onError: (error) => {
        errorModal(`Failed to add items to cart: ${error}`);
      }
    });
  };

  const handleRebatesClick = (event?: MouseEvent) => {
    event?.preventDefault();
    event?.stopPropagation();
    setRebatesForModal(
      effectiveRebates?.filter((rebate) => rebate.programs.length > 0) ?? []
    );
    setRebatesModalOpen(true);
    handleQuoteColumnClick(systemIndex);
  };

  return system ? (
    <div
      data-active={!isQuoted && quoteIDsToSend.includes(system.uniqueId)}
      className='flex w-full flex-col rounded-lg border-4 border-white bg-white p-5 shadow-lg ring-1 ring-black/5 transition-all data-[active=true]:border-blue-500'
    >
      <button
        type='button'
        className='flex cursor-pointer flex-col items-center border-gray-200 text-left outline-none focus:outline-none'
        onClick={isQuoted ? undefined : handleColumnClick}
        ref={animate}
      >
        <div className='flex w-full flex-col gap-5 border-b pb-5'>
          <div className='flex items-center justify-between'>
            {!isQuoted && (
              <div
                className='inline-block'
                onClick={(e) => e.stopPropagation()}
              >
                <Checkbox
                  value={quoteIDsToSend.includes(system.uniqueId)}
                  onChange={() => handleColumnClick()}
                />
              </div>
            )}
            {effectiveInStock === true && <Tag color='green' text='In Stock' />}
            {effectiveInStock === false && effectiveRestockDate && (
              <Tag
                color='yellow'
                text={`ETA ${new Date(effectiveRestockDate).toLocaleDateString(
                  'en-us',
                  {
                    month: 'numeric',
                    day: 'numeric'
                  }
                )}`}
              />
            )}
            {effectiveInStock === false && !effectiveRestockDate && (
              <Tag color='red' text='Out of Stock' />
            )}
            {isQuoted && effectiveInStock === undefined && (
              <div className='w-[80px]'>
                <Skeleton />
              </div>
            )}
          </div>
          <div className='flex flex-col items-center'>
            <div className='text-center text-lg font-bold'>{system.name}</div>
            <div className='text-center text-sm text-gray-500'>
              {system.configuration.name}
            </div>
          </div>
          <div className='flex flex-col'>
            <div className='text-center text-4xl font-bold'>
              {formatUSD(
                system.price +
                  system.quotableAccessories.reduce(
                    (total, acc) => total + acc.price * acc.quantity,
                    0
                  ) +
                  (projectDetails?.customerTaxExempt ? 0 : effectiveTax ?? 0),
                true
              )}
            </div>
            {projectDetails?.customerTaxExempt ? (
              <div className='text-center font-semibold text-gray-600'>
                (Tax exempt)
              </div>
            ) : (
              <div className='text-center font-semibold text-gray-500'>
                {effectiveTax !== undefined
                  ? `incl. ${formatUSD(effectiveTax, true)} tax`
                  : 'Calculating tax...'}
              </div>
            )}
          </div>
          {estimated14SeerCost !== null && (
            <div className='text-center'>
              <span>Save up to</span>
              <span className='mx-1 font-semibold text-green-500'>
                {formatUSD(estimated14SeerCost - (system.totalAnnualCost ?? 0))}
              </span>
              <span>annually compared to a typical 14 SEER system</span>
            </div>
          )}
          {effectiveTotalRebates !== undefined &&
            effectiveTotalRebates > 0 &&
            effectiveRebates && (
              <div className='flex justify-center text-center'>
                <a
                  role='button'
                  className='font-semibold text-blue-500 hover:text-blue-600'
                  onClick={(e) => handleRebatesClick(e)}
                >
                  Get up to {formatUSD(Math.floor(effectiveTotalRebates))} in
                  rebates for {locationData?.location ?? 'your location'}
                </a>
              </div>
            )}
          {!effectiveRebates &&
            !system.tags.includes(COMPATIBILITY_MATCH_TAG) && (
              <div className='flex justify-center text-center'>
                <div className='w-[350px]'>
                  <Skeleton color='blue' />
                </div>
              </div>
            )}
        </div>
        <div className='flex w-full flex-col gap-3 border-b py-5'>
          {effectiveShipping
            ? [
                ...effectiveShipping.shipOptions.rates.filter(
                  (r) => r.type === 'standard'
                ),
                ...effectiveShipping.shipOptions.rates
                  .filter((r) => r.type === 'guaranteed')
                  .sort(
                    (a, b) =>
                      new Date(a.deliverydate).getTime() -
                      new Date(b.deliverydate).getTime()
                  )
              ]
                .slice(0, 2)
                .map((option) => {
                  const location = locationData?.location
                    ? `${locationData.location} ${locationData.zipcode}`
                    : option.calculatedFor;
                  const name =
                    option.type === 'standard'
                      ? `Standard Shipping for ${location}`
                      : `Express Shipping for ${location}`;
                  const strippedLabel = option.label.replace(/<[^>]*>/g, '');
                  const formattedLabel =
                    option.type === 'standard'
                      ? `Arrives between ${strippedLabel}`
                      : strippedLabel;

                  return (
                    <div
                      key={option.id}
                      className='flex items-center justify-between gap-3'
                    >
                      <div>
                        <div className='mb-1 font-semibold'>{name}</div>
                        <div className='text-sm text-gray-600'>
                          {formattedLabel}
                        </div>
                      </div>
                      <div className='text-lg font-bold'>
                        {option.cost > 0
                          ? formatUSD(option.cost, true)
                          : 'Free'}
                      </div>
                    </div>
                  );
                })
            : [1, 2].map((i) => (
                <div key={i} className='flex items-center justify-between'>
                  <div>
                    <div className='mb-1 w-[300px]'>
                      <Skeleton />
                    </div>
                    <div className='w-[200px]'>
                      <Skeleton />
                    </div>
                  </div>
                  <div className='w-[50px]'>
                    <Skeleton />
                  </div>
                </div>
              ))}
        </div>
        {(system.heatingScore > 0 ||
          (system.coolingScore ?? 0) > 0 ||
          ductedCoverage) && (
          <div className='flex w-full flex-col gap-2 border-b py-5'>
            <div className='text-lg font-bold'>
              {ductedCoverage ? 'Coverage of Design Load' : 'Annual Coverage'}
            </div>
            {ductedCoverage ? (
              // Ducted design-load coverage (AHA-6796): "—" for null, the
              // design-load explainer always available via the info bubble
              // (the hours-weighted condenser text doesn't apply).
              <>
                <div className='flex items-center justify-between'>
                  <div className='flex items-center gap-2'>
                    <FireIcon className='h-5 w-5 text-orange-500' />
                    <span className='text-gray-600'>Heating</span>
                  </div>
                  <div className='flex items-center gap-1'>
                    <InfoTooltip>
                      <div className='max-w-[280px] text-center'>
                        {ductedCoverageTooltipText('heating', {
                          value: ductedCoverage.heatingCoveragePercent,
                          basis: ductedCoverage.basis
                        })}
                      </div>
                    </InfoTooltip>
                    <span className='font-semibold'>
                      {ductedPercentLabel(
                        ductedCoverage.heatingCoveragePercent
                      )}
                    </span>
                  </div>
                </div>
                <div className='flex items-center justify-between'>
                  <div className='flex items-center gap-2'>
                    <SparklesIcon className='h-5 w-5 text-sky-500' />
                    <span className='text-gray-600'>Cooling</span>
                  </div>
                  <div className='flex items-center gap-1'>
                    <InfoTooltip>
                      <div className='max-w-[280px] text-center'>
                        {ductedCoverageTooltipText('cooling', {
                          value: ductedCoverage.coolingCoveragePercent,
                          basis: ductedCoverage.basis
                        })}
                      </div>
                    </InfoTooltip>
                    <span className='font-semibold'>
                      {ductedPercentLabel(
                        ductedCoverage.coolingCoveragePercent
                      )}
                    </span>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className='flex items-center justify-between'>
                  <div className='flex items-center gap-2'>
                    <FireIcon className='h-5 w-5 text-orange-500' />
                    <span className='text-gray-600'>Heating</span>
                  </div>
                  <div className='relative'>
                    <div className='flex items-center gap-1'>
                      {system.heatingScore < 100 && (
                        <QuestionMarkCircleIcon className='h-5 w-5 text-gray-500' />
                      )}
                      <span className='font-semibold'>
                        {formatCoverageScore(system.heatingScore)}
                      </span>
                    </div>
                    {system.heatingScore < 100 && (
                      <Tooltip>
                        {system.condenserCoverage.map((condenser) => (
                          <div key={condenser.uniqueId} className='text-center'>
                            {system.configuration.condensers.length > 1 &&
                              `${condenser.name}: `}
                            insufficient{' '}
                            {condenser.percentageOfTimeOfInsufficientHeating ??
                              0}
                            % of the time, averaging{' '}
                            {Math.floor(
                              condenser.avgPartialHeatingCoveragePercent ?? 0
                            )}
                            % coverage during that time
                          </div>
                        ))}
                      </Tooltip>
                    )}
                  </div>
                </div>
                <div className='flex items-center justify-between'>
                  <div className='flex items-center gap-2'>
                    <SparklesIcon className='h-5 w-5 text-sky-500' />
                    <span className='text-gray-600'>Cooling</span>
                  </div>
                  <div className='relative'>
                    <div className='flex items-center gap-1'>
                      {system.coolingScore != null &&
                        system.coolingScore < 100 && (
                          <QuestionMarkCircleIcon className='h-5 w-5 text-gray-500' />
                        )}
                      <span className='font-semibold'>
                        {formatCoverageScore(system.coolingScore)}
                      </span>
                    </div>
                    {system.coolingScore != null &&
                      system.coolingScore < 100 && (
                        <Tooltip>
                          {system.condenserCoverage.map((condenser) => (
                            <div
                              key={condenser.uniqueId}
                              className='text-center'
                            >
                              {system.configuration.condensers.length > 1 &&
                                `${condenser.name}: `}
                              insufficient{' '}
                              {condenser.percentageOfTimeOfInsufficientCooling ??
                                0}
                              % of the time, averaging{' '}
                              {Math.floor(
                                condenser.avgPartialCoolingCoveragePercent ?? 0
                              )}
                              % coverage during that time
                            </div>
                          ))}
                        </Tooltip>
                      )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
        <div className='flex w-full flex-col gap-2 border-b py-5'>
          <div className='text-lg font-bold'>Warranty Information</div>
          <div className='flex items-center justify-between'>
            <span className='text-gray-600'>Parts</span>
            <span className='font-semibold'>
              {system.configuration.condensers
                .flatMap((c) => c.zones)
                .find((z) =>
                  z.selectedProduct?.warranties?.some((w) => w.id === 74)
                )
                ?.selectedProduct?.warranties?.find((w) => w.id === 74)
                ?.value.split(' ')[0] ?? 'N/A'}{' '}
              years
            </span>
          </div>
          <div className='flex items-center justify-between'>
            <span className='text-gray-600'>Compressor</span>
            <span className='font-semibold'>
              {system.configuration.condensers
                .find((c) =>
                  c.selectedProduct?.warranties?.some((w) => w.id === 40)
                )
                ?.selectedProduct?.warranties?.find((w) => w.id === 40)
                ?.value.split(' ')[0] ?? 'N/A'}{' '}
              years
            </span>
          </div>
        </div>
        <div className='flex w-full flex-col gap-5 py-5'>
          {system.configuration.condensers.map((condenser) => (
            <SystemMap
              key={condenser.uniqueId}
              condenser={condenser}
              showPrices
              showSpaces
              renderAfterZone={(zoneUniqueId) => {
                const zone = condenser.zones.find(
                  (z) => z.uniqueId === zoneUniqueId
                );
                if (!zone || (!quotableSystemsData && !quotableSystemsFailed))
                  return null;
                let quotedAccessories;
                if (quotableSystemsData) {
                  quotedAccessories =
                    zoneQuotedAccessories.get(zone.uniqueId) ?? [];
                } else if (concealedZoneCount <= 1) {
                  quotedAccessories = system.quotableAccessories;
                }
                return (
                  <ZoneDuctingProducts
                    configId={system.configuration.uniqueId}
                    condenserUniqueId={condenser.uniqueId}
                    zone={zone}
                    quotedAccessories={quotedAccessories}
                  />
                );
              }}
            />
          ))}
        </div>
        {showAllItems &&
          system.quotableAccessories.some((acc) => acc.quantity > 0) && (
            <div className='flex w-full flex-col gap-5 pb-5'>
              <div className='flex items-center gap-2'>
                <RectangleGroupIcon className='h-6 w-6 text-gray-800' />
                <span className='text-xl font-semibold'>Accessories</span>
              </div>
              <div className='flex flex-col gap-7'>
                {system.quotableAccessories
                  .filter((acc) => acc.quantity > 0)
                  .map((accessory) => (
                    <div
                      key={accessory.productId}
                      className='flex items-center gap-4'
                    >
                      <ProductPhoto product={accessory} />
                      <div className='flex grow flex-col gap-1'>
                        <ProductLink
                          type='accessory'
                          product={accessory}
                          quantity={accessory.quantity}
                        />
                        <span className='text-xs text-gray-600'>
                          {accessory.description}
                        </span>
                      </div>
                      <div className='pl-3 text-lg font-bold'>
                        {formatUSD(accessory.price * accessory.quantity, true)}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}
      </button>
      <div className='flex flex-col gap-5 pt-5'>
        {system.quotableAccessories.some((acc) => acc.quantity > 0) && (
          <Button
            text={showAllItems ? 'Show Fewer Items' : 'Show All Items'}
            color='secondary'
            size='lg'
            fullWidth
            onClick={handleShowAllItems}
          />
        )}
        <div
          id={
            !isQuoted && systemIndex === 0 ? 'quote-column-actions' : undefined
          }
          className='flex flex-col gap-5'
        >
          <Button
            text={getBuyNowButtonText()}
            size='lg'
            fullWidth
            onClick={handleBuyNow}
            disabled={!isQuoted && isAddingToCart}
          />
          {!isQuoted && (
            <Button
              text='Send this Quote Only'
              color='green'
              size='lg'
              fullWidth
              disabled={system.quoteStatus === 'sending' || emailModalOpen}
              onClick={() => setEmailModalOpen(true)}
            />
          )}
        </div>
      </div>
      <EmailSingleQuoteModal system={system} openState={emailModalOpenState} />
    </div>
  ) : null;
}
